import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import Close from '@mui/icons-material/Close';
import FolderSharp from '@mui/icons-material/FolderSharp';
import ListIcon from '@mui/icons-material/List';
import Delete from '@mui/icons-material/Delete';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import StyledCollectionEntitiesActionbar
  from 'components/organisms/Snackbars/CollectionEntitiesActionbar/CollectionEntitiesActionbar.styles';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import EntitiesTaskAddDialog from 'components/organisms/Dialogs/EntitiesTaskAddDialog/EntitiesTaskAddDialog';
import CollectionSelectionDialog
  from 'components/organisms/Dialogs/CollectionSelectionDialog/CollectionSelectionDialog';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import Download from '@mui/icons-material/Download';
import BackupTable from '@mui/icons-material/BackupTable';
import FolderZip from '@mui/icons-material/FolderZip';
import utils from 'helpers/utils';
import constants from 'helpers/constants';
import Add from '@mui/icons-material/Add';
import Edit from '@mui/icons-material/Edit';
import {useAuthClient, useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';
import {useCollectionGet} from 'services/collection/collection.hooks';
import EntitiesBulkDialog from 'components/organisms/Dialogs/EntitiesBulkDialog/EntitiesBulkDialog';
import EntitiesExportSettingsDialog
  from 'components/organisms/Dialogs/EntitiesExportSettingsDialog/EntitiesExportSettingsDialog';
import Code from '@mui/icons-material/Code';

const CollectionEntitiesActionbar = React.forwardRef((props, ref) => {
  const {
    tableProvider,
    listSelection,
    data,
    ...innerProps
  } = useComponentProps(props, 'CollectionEntitiesActionbar');

  const authorize = useAuthorize();
  const snackbar = useSnackbar();
  const dialogControl = useDialogControl();

  const listState = useEffectItem(tableProvider.appliedListState());

  const entities = listSelection.dataSelection;

  const collection = tableProvider.context?.data;
  const client = useAuthClient();
  const universeCollection = useCollectionGet({collectionId: client?.universeCollectionId},
    {enabled: Boolean(client?.universeCollectionId)})?.data;

  const isSuggestion = Boolean(tableProvider?.view?.activeViews?.find((v) => v.name === 'suggestions'));
  const isRejection = Boolean(tableProvider?.view?.activeViews?.find((v) => v.name === 'rejections'));
  const clearSelectionEvent = useEffectEvent(listSelection.clearSelection);
  const filterSelectionEvent = useEffectEvent(listSelection.filterSelection);
  const entityUnAssignEvent = useEffectEvent(tableProvider.updaters?.unAssignItems);
  const entityAssignEvent = useEffectEvent(tableProvider.updaters?.assignItems);
  const entityAcceptEvent = useEffectEvent(tableProvider.updaters?.acceptItems);
  const entityDownloadEvent = useEffectEvent(tableProvider.updaters?.downloadItems);
  const entitySelectedEvent = useEffectEvent(tableProvider.updaters?.selectedItems);
  const entityApplyActionsEvent = useEffectEvent(tableProvider.updaters?.applyActions);
  const entityCreateTaskEvent = useEffectEvent(tableProvider.updaters?.createTask);

  const actions = useMemo(() => {
    const actions = [];

    actions.push({
      label: 'Remove selection',
      icon: Close,
      ButtonProps: {
        variant: 'outlined',
        color: 'white'
      },
      onClick: () => {
        clearSelectionEvent?.();
      }
    });

    let removeAction, editAction;
    if (listSelection.count <= listSelection.max) {
      const bulkActions = constants.data.entityBulkActions
        .filter((eba) => {
          return (collection?.tagGroups?.length > 0 || ![constants.entity.bulkActions.setCollectionTags].includes(eba.value)) &&
            (universeCollection?.tagGroups?.length > 0 || ![constants.entity.bulkActions.setClientTags].includes(eba.value));
        })
        .filter((eba) => eba.edit && authorize({attribute: `collection.entity.bulk.${eba.value}`}));

      editAction = {
        label: `Edit compan${listSelection.count === 1 ? 'y' : 'ies'}`,
        auth: bulkActions.length > 0 ? utils.createAuth({attribute: 'collection.entity.bulk'}) :
          utils.createAuth({attribute: 'system.null'}),
        icon: Edit,
        onClick: (e) => {
          let selected, selectedFailed = false;

          const handleClose = (e, reason) => {
            if (reason === 'saveButtonClick') {
              clearSelectionEvent?.();
            }
            dialogControl.hide();
          };

          const handleSubmit = (action) => {
            const actions = [{
              action: constants.entity.bulkActions.applyToExisting,
              collection: {
                name: collection.name,
                collectionId: collection.collectionId
              }
            }].concat(action);

            const message = `Saving the compan${listSelection.count === 1 ? 'y' : 'ies'}`;
            return utils.observePromise(utils.wait(() => {
              if (selectedFailed) {
                return Promise.reject();
              } else {
                return utils.isDefined(selected);
              }
            }, constants.delay.selection).then(() => {
              return entityApplyActionsEvent?.({entityIds: selected.map((e) => +e.entityId), actions});
            }), () => {
              return snackbar.show(`${message}...`, null, {
                color: 'info',
                autoHideDuration: null
              });
            }, () => {
              return snackbar.show(`The compan${listSelection.count === 1 ? 'y' : 'ies'} were saved`, null,
                {color: 'success', autoHideDuration: constants.delay.success});
            }, () => {
              return snackbar.show(`${message} failed`, null,
                {color: 'error', autoHideDuration: constants.delay.error});
            }, (id) => {
              snackbar.hide(id);
            });
          }

          dialogControl.show(<EntitiesBulkDialog placement="top"
                                                 count={listSelection.count}
                                                 bulkActions={bulkActions}
                                                 collection={collection}
                                                 universeCollection={universeCollection}
                                                 fieldData={tableProvider.fieldData}
                                                 onSubmit={handleSubmit}/>, true, handleClose);

          if (listSelection.count !== entities.length) {
            entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
              selected = filterSelectionEvent?.(entities);
            }, () => {
              dialogControl.hide();
              selectedFailed = true;
              snackbar.show('Retrieving selection data failed', null,
                {color: 'error', autoHideDuration: constants.delay.error});
            });
          } else {
            selected = entities;
          }

          e.preventDefault();
        }
      };

      removeAction = {
        label: isSuggestion ? `Remove suggestion${listSelection.count === 1 ? '' : 's'}` : 'Remove from collection',
        auth: utils.createAuth({attribute: 'collection.update', meta: {collection}}),
        icon: Delete,
        ButtonProps: {
          color: 'error'
        },
        onClick: (e) => {
          let selected, selectedFailed = false;
          const handleConfirm = () => {
            return utils.wait(() => {
              if (selectedFailed) {
                return Promise.reject();
              } else {
                return utils.isDefined(selected);
              }
            }, constants.delay.selection).then(() => {
              const entityIds = selected?.map((e) => +e.entityId);

              return entityUnAssignEvent?.({entityIds})
                .then(() => {
                  clearSelectionEvent?.();
                });
            })
              .catch(() => {
                throw new Error(`Removing ${isSuggestion ? `suggestion${listSelection.count === 1 ? '' : 's'}` : `compan${listSelection.count === 1 ? 'y' : 'ies'}`} from collection failed`);
              })
          }

          const question = `Are you sure you want to remove ${listSelection.count} ${isSuggestion ? `suggestion${listSelection.count === 1 ? '' : 's'}` : `compan${listSelection.count === 1 ? 'y' : 'ies'}`} from collection '${collection.name}'?`;
          const explanation = `The compan${listSelection.count === 1 ? 'y' : 'ies'} will added to the rejections list`

          dialogControl.show(<ConfirmDialog question={question}
                                            explanation={explanation}
                                            onConfirm={handleConfirm}
                                            ConfirmButtonProps={{
                                              children: `Remove ${isSuggestion ? `suggestion${listSelection.count === 1 ? '' : 's'}` : `compan${listSelection.count === 1 ? 'y' : 'ies'}`}`,
                                              color: 'error'
                                            }}/>, true);

          if (listSelection.count !== entities.length) {
            entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
              selected = filterSelectionEvent?.(entities);
            }, () => {
              dialogControl.hide();
              selectedFailed = true;
              snackbar.show('Retrieving selection data failed', null,
                {color: 'error', autoHideDuration: constants.delay.error});
            });
          } else {
            selected = entities;
          }

          e.preventDefault();
        }
      };

      if (isSuggestion) {
        actions.push(removeAction);
      }

      if (isSuggestion || isRejection) {
        actions.push({
          label: 'Add to collection',
          auth: utils.createAuth({attribute: 'collection.update'}),
          icon: Add,
          onClick: (e) => {
            const acceptEntities = (entities) => {
              entityAcceptEvent?.({entityIds: entities?.map((e) => +e.entityId)})
                .then(() => {
                  clearSelectionEvent?.();
                })
                .catch(() => {
                  snackbar.show(`Adding the ${isSuggestion ? `suggestion${entities.length === 1 ? '' : 's'}` : `compan${entities.length === 1 ? 'y' : 'ies'}`} to collection '${collection.name}' failed`, null,
                    {color: 'error', autoHideDuration: constants.delay.error});
                });
            };

            if (listSelection.count === entities.length) {
              acceptEntities(entities);
            } else {
              entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
                acceptEntities(filterSelectionEvent?.(entities));
              }, () => {
                dialogControl.hide();
                snackbar.show('Retrieving selection data failed', null,
                  {color: 'error', autoHideDuration: constants.delay.error});
              });
            }

            e.preventDefault();
          }
        });
      }

      actions.push({
        label: 'Add to other collection',
        auth: utils.createAuth({attribute: 'collection.update'}),
        icon: FolderSharp,
        onClick: (e) => {
          let selected, selectedFailed = false;

          const handleClose = (e, reason) => {
            if (reason === 'saveButtonClick') {
              clearSelectionEvent?.();
            }
            dialogControl.hide();
          };

          const handleSubmit = (addedCollections) => {
            const message = `Adding the compan${listSelection.count === 1 ? 'y' : 'ies'} to the collection${addedCollections.length === 1 ? '' : 's'}`;
            return utils.observePromise(utils.wait(() => {
              if (selectedFailed) {
                return Promise.reject();
              } else {
                return utils.isDefined(selected);
              }
            }, constants.delay.selection).then(() => {
              return Promise.all(addedCollections.map((collection) => {
                return entityAssignEvent?.({
                  collectionId: collection.collectionId,
                  entityIds: selected?.map((e) => +e.entityId)
                });
              }));
            }), () => {
              return snackbar.show(`${message}...`, null, {
                color: 'info',
                autoHideDuration: null
              });
            }, () => {
              return snackbar.show(`The compan${listSelection.count === 1 ? 'y' : 'ies'} ${listSelection.count === 1 ? 'was' : 'were'} added to the collection${addedCollections.length === 1 ? '' : 's'}`, null,
                {color: 'success', autoHideDuration: constants.delay.success});
            }, () => {
              return snackbar.show(`${message} failed`, null,
                {color: 'error', autoHideDuration: constants.delay.error});
            }, (id) => {
              snackbar.hide(id);
            });
          };

          const showDialog = (collections) => {
            dialogControl.show(<CollectionSelectionDialog placement="top"
                                                          title="Add to collection"
                                                          subtitle={`For ${utils.formatNumber(listSelection.count)} compan${listSelection.count === 1 ? 'y' : 'ies'}`}
                                                          collections={collections}
                                                          hideCollections={true}
                                                          onSubmit={handleSubmit}/>, true, handleClose);
          }

          if (listSelection.count === entities.length) {
            const collections = entities.reduce((a, e) => {
              e.collections.forEach((ce) => {
                if (!a.find((c) => +c.collectionId === +ce.collectionId)) {
                  if (!entities.some((e1) => !e1.collections.find((c) => +c.collectionId === +ce.collectionId))) {
                    a.push(ce);
                  }
                }
              })
              return a;
            }, []);

            selected = entities;
            showDialog(collections);
          } else {
            showDialog();
            entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
              selected = filterSelectionEvent?.(entities);
            }, () => {
              dialogControl.hide();
              selectedFailed = true;
              snackbar.show('Retrieving selection data failed', null,
                {color: 'error', autoHideDuration: constants.delay.error});
            });
          }

          e.preventDefault();
        }
      });

      if (!isSuggestion && !isRejection) {
        actions.push({
          label: 'Add task',
          auth: utils.createAuth({attribute: 'collection.entity.task.create'}),
          icon: ListIcon,
          onClick: (e) => {
            let selected, selectedFailed = false;

            const handleClose = (e, reason) => {
              if (reason === 'saveButtonClick') {
                clearSelectionEvent?.();
              }
              dialogControl.hide();
            };

            const handleSubmit = (task) => {
              return utils.wait(() => {
                if (selectedFailed) {
                  return Promise.reject();
                } else {
                  return utils.isDefined(selected);
                }
              }, constants.delay.selection).then(() => {
                const message = `Adding the task for the compan${listSelection.count === 1 ? 'y' : 'ies'}`;
                return utils.observePromise(utils.wait(() => {
                  if (selectedFailed) {
                    return Promise.reject();
                  } else {
                    return utils.isDefined(selected);
                  }
                }, constants.delay.selection).then(() => {
                  return entityCreateTaskEvent?.(selected, task);
                }), () => {
                  return snackbar.show(`${message}...`, null, {
                    color: 'info',
                    autoHideDuration: null
                  });
                }, () => {
                  return snackbar.show(`The task was added to the compan${listSelection.count === 1 ? 'y' : 'ies'}`, null,
                    {color: 'success', autoHideDuration: constants.delay.success});
                }, () => {
                  return snackbar.show(`${message} failed`, null,
                    {color: 'error', autoHideDuration: constants.delay.error});
                }, (id) => {
                  snackbar.hide(id);
                });
              });
            }

            const showDialog = () => {
              dialogControl.show(<EntitiesTaskAddDialog placement="top"
                                                        count={listSelection.count}
                                                        onSubmit={handleSubmit}/>, true, handleClose);
            }

            showDialog();
            if (listSelection.count !== entities.length) {
              entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
                selected = filterSelectionEvent?.(entities);
              }, () => {
                dialogControl.hide();
                selectedFailed = true;
                snackbar.show('Retrieving selection data failed', null,
                  {color: 'error', autoHideDuration: constants.delay.error});
              });
            } else {
              selected = entities;
            }

            e.preventDefault();
          }
        });
      }

      actions.push(editAction);
    }

    if (!isSuggestion && !isRejection) {
      actions.push({
        label: 'Download',
        auth: utils.createAuth({attribute: 'collection.entity.download'}),
        icon: Download,
        ActionButtonProps: {
          DropDownPopperProps: {
            ContextPopperProps: {
              density: 'densest',
              transparent: true,
              outlined: false,
              shadows: false,
              strategy: 'fixed',
              stretch: false,
              offset: [0, 8]
            }
          }
        },
        children: [
          {
            label: 'Download .xlsx',
            auth: utils.createAuth({attribute: 'collection.entity.download.xlsx'}),
            icon: BackupTable,
            onClick: (e) => {
              let selected, selectedFailed = false;

              const handleClose = (e, reason) => {
                if (reason === 'saveButtonClick') {
                  clearSelectionEvent?.();
                }
                dialogControl.hide();
              };

              const handleSubmit = (settings) => {
                const downloadingSnackbar = snackbar.show('Downloading company data...', null, {
                  color: 'info',
                  autoHideDuration: null
                });

                utils.wait(() => {
                  if (selectedFailed) {
                    return Promise.reject();
                  } else {
                    return utils.isDefined(selected);
                  }
                }, constants.delay.selection).then(() => {
                  const filename = `${collection.name}.xlsx`;

                  const fields = settings.columnSelection !== 'selectedColumns' ? null :
                    Object.keys(tableProvider.columnState.columnVisibility ?? {})
                      .filter((k) => tableProvider.columnState.columnVisibility[k])
                      .map((k) => k.split('|')[0]);

                  entityDownloadEvent?.({
                    filename: filename,
                    fields: fields,
                    entityIds: selected.map((e) => e.entityId),
                    filters: {
                      ...utils.filter2Object(listState.filter),
                      search: listState.search
                    }
                  })
                    .then((res) => {
                      utils.bytesDownload(res.response.data, filename, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
                    });
                })
                  .catch((error) => {
                    if (error?.response?.status === constants.http.status.conflict) {
                      snackbar.show('The company export limit has been reached', null,
                        {color: 'warning', autoHideDuration: constants.delay.warning});
                    } else {
                      snackbar.show(`Downloading compan${listSelection.count === 1 ? 'y' : 'ies'} failed`, null,
                        {color: 'error', autoHideDuration: constants.delay.error});
                    }
                  })
                  .finally(() => {
                    snackbar.hide(downloadingSnackbar);
                  });
              }

              const showDialog = () => {
                dialogControl.show(<EntitiesExportSettingsDialog placement="top"
                                                                 count={listSelection.count}
                                                                 onSubmit={handleSubmit}/>, true, handleClose);
              }

              showDialog();
              if (listSelection.count !== entities.length) {
                entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
                  selected = filterSelectionEvent?.(entities);
                }, () => {
                  dialogControl.hide();
                  selectedFailed = true;
                  snackbar.show('Retrieving selection data failed', null,
                    {color: 'error', autoHideDuration: constants.delay.error});
                });
              } else {
                selected = entities;
              }

              e.preventDefault();
            }
          },
          {
            label: 'Download .json',
            auth: utils.createAuth({attribute: 'collection.entity.download.json'}),
            icon: Code,
            onClick: (e) => {
              const doDownload = (selected) => {
                const filename = `${collection.name}.json`;

                entityDownloadEvent?.({
                  json: true,
                  filename: filename,
                  entityIds: selected.map((e) => e.entityId),
                  filters: {
                    ...utils.filter2Object(listState.filter),
                    search: listState.search
                  }
                })
                  .then(() => {
                    snackbar.show('Json download is scheduled', null,
                      {color: 'success', autoHideDuration: constants.delay.success});
                    clearSelectionEvent?.();
                  })
                  .catch((error) => {
                    if (error?.response?.status === constants.http.status.conflict) {
                      snackbar.show('The company export limit has been reached', null,
                        {color: 'warning', autoHideDuration: constants.delay.warning});
                    } else {
                      snackbar.show(`Download scheduling failed`, null,
                        {color: 'error', autoHideDuration: constants.delay.error});
                    }
                  });
              }

              if (listSelection.count !== entities.length) {
                entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
                  doDownload(filterSelectionEvent?.(entities));
                }, () => {
                  dialogControl.hide();
                  snackbar.show('Retrieving selection data failed', null,
                    {color: 'error', autoHideDuration: constants.delay.error});
                });
              } else {
                doDownload(entities);
              }

              e.preventDefault();
            }
          },
          {
            label: 'Download .zip',
            auth: utils.createAuth({attribute: 'collection.entity.download.zip'}),
            icon: FolderZip,
            onClick: (e) => {
              const doDownload = (selected) => {
                const filename = `${collection.name}.zip`;

                entityDownloadEvent?.({
                  zip: true,
                  filename: filename,
                  entityIds: selected.map((e) => e.entityId),
                  filters: {
                    ...utils.filter2Object(listState.filter),
                    search: listState.search
                  }
                })
                  .then(() => {
                    snackbar.show('Zip download is scheduled', null,
                      {color: 'success', autoHideDuration: constants.delay.success});
                    clearSelectionEvent?.();
                  })
                  .catch((error) => {
                    if (error?.response?.status === constants.http.status.conflict) {
                      snackbar.show('The company export limit has been reached', null,
                        {color: 'warning', autoHideDuration: constants.delay.warning});
                    } else {
                      snackbar.show(`Download scheduling failed`, null,
                        {color: 'error', autoHideDuration: constants.delay.error});
                    }
                  });
              }

              if (listSelection.count !== entities.length) {
                entitySelectedEvent?.(listState, 0, listSelection.dataCount, null, false, true, (entities) => {
                  doDownload(filterSelectionEvent?.(entities));
                }, () => {
                  dialogControl.hide();
                  snackbar.show('Retrieving selection data failed', null,
                    {color: 'error', autoHideDuration: constants.delay.error});
                });
              } else {
                doDownload(entities);
              }

              e.preventDefault();
            }
          }
        ]
      });

      if (removeAction) {
        actions.push(removeAction);
      }
    }

    return actions;
  }, [listSelection.count, listSelection.dataCount, listSelection.max, listState, collection, universeCollection, entities, authorize,
    tableProvider.columnState, tableProvider.fieldData, clearSelectionEvent, filterSelectionEvent, snackbar, dialogControl, entitySelectedEvent,
    isSuggestion, isRejection, entityApplyActionsEvent, entityDownloadEvent, entityUnAssignEvent, entityAssignEvent,
    entityAcceptEvent, entityCreateTaskEvent]);

  const renderTitle = () => {
    const count = utils.formatNumber(listSelection.count ?? 0);
    const title = `${count} compan${listSelection.count === 1 ? 'y' : 'ies'}`;
    if (actions?.length > 1) {
      return title;
    } else {
      const max = utils.formatNumber(listSelection.max ?? 0);
      return `${title} (max ${max})`
    }
  }

  return <StyledCollectionEntitiesActionbar ref={ref} {...innerProps}
                                            title={renderTitle()}
                                            actions={actions} />
});

CollectionEntitiesActionbar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  tableProvider: PropTypes.any,
  listSelection: PropTypes.object,
  data: PropTypes.array
};

export default CollectionEntitiesActionbar;
